// 🌐 SIMPLIFIED HTTP CLIENT
// ✅ Automatic retry logic with exponential backoff
// ✅ Timeout handling with AbortController
// ✅ Authentication via httpOnly cookies (automatic)
// ✅ Basic error handling for 401s

export interface HttpClientConfig {
  baseUrl?: string;
  timeout?: number;
  retries?: number;
}

class HttpClient {
  private baseUrl: string;
  private timeout: number;
  private retries: number;

  constructor(config: HttpClientConfig = {}) {
    // In production, VITE_API_URL is the domain without /api suffix
    // We need to append /api to form the full API base URL
    const envUrl = import.meta.env.VITE_API_URL;
    this.baseUrl = config.baseUrl || (envUrl ? `${envUrl}/api` : '/api');
    this.timeout = config.timeout || 10000;
    this.retries = config.retries || 2;
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    // Authentication handled by httpOnly cookies (sent automatically by browser)
    // No need to manually add Authorization header
    
    const requestOptions: RequestInit = {
      ...options,
      credentials: 'include', // Important: Send cookies with requests
      headers: {
        // Only set Content-Type for requests with a body
        ...(options.body && { 'Content-Type': 'application/json' }),
        ...options.headers
      }
    };

    let lastError: Error;
    
    // Retry logic
    for (let attempt = 0; attempt <= this.retries; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeout);
        
        const response = await fetch(url, {
          ...requestOptions,
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        // Handle authentication errors
        if (response.status === 401) {
          console.warn('401 Unauthorized - Authentication required');
          throw new Error('Authentication required');
        }
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        // Handle responses with no content (like DELETE operations)
        if (response.status === 204 || response.headers.get('content-length') === '0') {
          return undefined as T;
        }
        
        // Check if response has content to parse
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          return data;
        }

        // For non-JSON responses, throw an error since API clients expect JSON
        throw new Error(`Unexpected response content-type: ${contentType || 'none'}. Expected application/json`);
        
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Request failed');
        
        // Don't retry on auth errors or client errors (4xx)
        if (error instanceof Error) {
          if (error.message.includes('401') || 
              error.message.includes('Authentication') ||
              error.message.includes('HTTP 4')) {
            throw error;
          }
        }
        
        // Wait before retry (exponential backoff)
        if (attempt < this.retries) {
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        }
      }
    }
    
    throw lastError!;
  }

  // Convenience methods
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    // Debug logging only in development
    if (import.meta.env.DEV) {
      console.log('🐛 DEBUG: HTTP Client - POST request');
      console.log('🐛 DEBUG: HTTP Client - endpoint:', endpoint);
      console.log('🐛 DEBUG: HTTP Client - raw data:', data);
      console.log('🐛 DEBUG: HTTP Client - stringified body:', data ? JSON.stringify(data) : undefined);
    }

    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  async delete<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      body: data ? JSON.stringify(data) : undefined
    });
  }
}

export const httpClient = new HttpClient();